import { Quantity } from '../Common/Quantity.js'
import { Units, getLabel } from '../Common/Units.js'

// Plain-text CSV of a Residential project's load results, for the AI
// assistant (see get_house_results in src/Assistant/Tools.js). Mirrors
// getSystemResultsCsv() for Commercial Systems: one '# Sheet: <name>' section
// per table, covering what the Results tab shows — the summary, the load
// breakdown table, the per-component breakdown, and the inputs summary.
// All values are in the project's display units.

function csvCell(value) {
  const str = value == null ? '' : String(value)
  return str.includes(',') || str.includes('"') || str.includes('\n')
    ? `"${str.replace(/"/g, '""')}"`
    : str
}

function csvRow(cells) {
  return cells.map(csvCell).join(',')
}

function unitsLabel(units) {
  return units ? getLabel(units) : ''
}

function summaryLines(results) {
  const rows = results.getSummaryNode()?.data?.rows ?? []
  return [
    '# Sheet: Summary',
    csvRow(['Item', 'Value', 'Units']),
    // SummaryTableNode already converted numeric rows to project units.
    ...rows.map(row => csvRow([row.label, row.value, unitsLabel(row.units)])),
  ]
}

function loadBreakdownLines(results) {
  const units = unitsLabel(results.getOutputUnits())
  const lines = [
    '# Sheet: Load Breakdown',
    csvRow(['Group', 'Item', `Cooling Sensible (${units})`, `Cooling Latent (${units})`,
      `Heating Sensible (${units})`, `Heating Latent (${units})`]),
  ]
  // Already converted to getOutputUnits() by ResidentialProject._calcResults().
  for (const [groupName, group] of Object.entries(results.getOutputs() ?? {})) {
    for (const [itemName, load] of Object.entries(group)) {
      lines.push(csvRow([groupName, itemName, round(load.cooling.sensible), round(load.cooling.latent),
        round(load.heating.sensible), round(load.heating.latent)]))
    }
  }
  return lines
}

function round(value) {
  return value == null ? '' : Math.round(value)
}

// The per-component breakdown (e.g. each wall, window and door) that the
// Results tab's Details charts are built from. Values are in Units.Load, not
// yet converted; each entry is {label, value: {cooling, heating}, children?},
// and an entry may only have one of cooling/heating (e.g. a window's).
function componentBreakdownLines(results) {
  const targetUnits = Quantity.getConvertedUnits(Units.Load)
  const units = unitsLabel(targetUnits)
  const convert = (value) => value == null ? '' : Math.round(Quantity.convertValue(value, Units.Load, targetUnits))
  const lines = [
    '# Sheet: Component Breakdown',
    csvRow(['Category', 'Component', `Cooling Sensible (${units})`, `Cooling Latent (${units})`,
      `Heating Sensible (${units})`, `Heating Latent (${units})`]),
  ]
  const addEntries = (breakdown, category) => {
    for (const entry of Object.values(breakdown ?? {})) {
      const value = entry.value ?? {}
      lines.push(csvRow([category ?? entry.label, category ? entry.label : 'Total',
        convert(value.cooling?.sensible), convert(value.cooling?.latent),
        convert(value.heating?.sensible), convert(value.heating?.latent)]))
      if (entry.children) {
        addEntries(entry.children, category ?? entry.label)
      }
    }
  }
  addEntries(results.getDetailsNode()?.combinedBreakdown, null)
  return lines
}

function inputsLines(results) {
  const lines = [
    '# Sheet: Inputs Summary',
    csvRow(['Group', 'Input', 'Value', 'Units']),
  ]
  for (const [groupName, group] of Object.entries(results.getInputs() ?? {})) {
    for (const outputValue of Object.values(group)) {
      lines.push(csvRow([groupName, outputValue.getPlainLabel(), outputValue.getValueStr(),
        outputValue.getDisplayUnitsLabel()]))
    }
  }
  return lines
}

// `results` is a ResidentialResults that calculated successfully (no project
// errors or calculation error).
export function getResidentialResultsCsv(results) {
  const info = [
    '# Sheet: Info',
    csvRow(['Project', results.getProjectName()]),
    csvRow(['Location', results.getLocationName()]),
    csvRow(['Calculated On', results.getDateCalculatedOn()]),
  ]
  return [
    ...info,
    ...summaryLines(results),
    ...loadBreakdownLines(results),
    ...componentBreakdownLines(results),
    ...inputsLines(results),
  ].join('\n')
}
